import { env } from '../environment';
import { EnvironmentTypes } from '../enums';
import { getTopOrigin } from '../helpers/iframe';
import { isMobile } from '../helpers/is-mobile';
import type { LaunchData } from '../models';

/**
 * Launch beacon: ONE OTLP/JSON span per launch ("launch.web"), joined to the backend trace through the
 * `traceparent` the launcher puts in the redirect URL. No SDK. Nothing here may ever break a launch,
 * so every exported function swallows its own errors.
 */

export type LaunchOutcome = 'game_ready' | 'unavailable' | 'error' | 'stalled' | 'abandoned';
export type LaunchStep = 'info' | 'preLobby' | 'auth' | 'socket' | 'iframeSet' | 'iframeLoaded' | 'frameRetry' | 'gameLoaded' | 'ready';

type AttrValue = string | number | boolean;

const SPAN_NAME = 'launch.web';
// No progress for this long is reported as `stalled` with the last step reached; every step re-arms it.
const WATCHDOG_MS = 30000;
const MAX_ERROR_MESSAGE = 500;
const TRACEPARENT = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/i;

const state = {
  started: false,
  finished: false,
  sampled: false,
  t0Epoch: 0,
  t0Perf: 0,
  traceId: '',
  spanId: '',
  parentSpanId: '',
  launchToken: '',
  authToken: '',
  traceSender: null as ((payloadJson: string) => boolean) | null,
  lastStep: 'boot' as LaunchStep | 'boot',
  steps: {} as Partial<Record<LaunchStep, number>>,
  attrs: {} as Record<string, AttrValue>,
  errorReason: '',
  errorMessage: '',
  errorCount: 0,
  watchdog: null as ReturnType<typeof setTimeout> | null
};

// The game's own path, in order. auth/preLobby/socket are side branches that finish in any order, so the
// most recent step says nothing about how far the game got; `launch.stalledAt` reports the furthest of these.
const GAME_PATH: LaunchStep[] = ['info', 'iframeSet', 'iframeLoaded', 'gameLoaded', 'ready'];

const furthestGameStep = (): LaunchStep | 'boot' => {
  let furthest: LaunchStep | 'boot' = 'boot';
  GAME_PATH.forEach(step => {
    if (state.steps[step] !== undefined) furthest = step;
  });
  return furthest;
};

const now = (): number =>
  typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

const randomHex = (bytes: number): string => {
  const arr = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < bytes; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < bytes; i++) out += (arr[i] < 16 ? '0' : '') + arr[i].toString(16);
  return out;
};

// int64 nanoseconds as a decimal string: ms * 1e6 loses precision in a double, so append zeros instead.
const toNano = (epochMs: number): string => `${Math.floor(epochMs)}000000`;

const setAttr = (key: string, value: AttrValue | null | undefined): void => {
  if (value === null || value === undefined || value === '') return;
  state.attrs[key] = value;
};

const describe = (error: unknown): string => {
  if (error === null || error === undefined) return '';
  if (typeof error !== 'object') return String(error);
  // GameErrorPayload / Error shapes: keep code + message together; an empty errorMessage must not hide the code.
  const e = error as { message?: unknown; errorMessage?: unknown; errorCode?: unknown; launcherFrontErrorCode?: unknown };
  const parts = [e.errorCode, e.launcherFrontErrorCode, e.errorMessage || e.message]
    .filter(part => part !== undefined && part !== null && part !== '')
    .map(part => String(part));
  if (parts.length) return parts.join(' ');

  // Nothing readable: keep the type and the first stack frame instead of an empty string.
  const name = (error as { name?: string }).name || (error as object).constructor?.name || 'Object';
  const stack = typeof (error as { stack?: unknown }).stack === 'string'
    ? String((error as { stack: string }).stack).split('\n')[1]?.trim() ?? ''
    : '';
  try {
    const json = JSON.stringify(error);
    if (json && json !== '{}') return `${name} ${json}`.slice(0, MAX_ERROR_MESSAGE);
  } catch {
    /* circular */
  }
  return (stack ? `${name} @ ${stack}` : name).slice(0, MAX_ERROR_MESSAGE);
};

const collectReturnPath = (returnPath: unknown): void => {
  if (typeof returnPath !== 'string' || !returnPath) return;
  setAttr('launch.returnPathPresent', true);
  const q = returnPath.indexOf('?');
  if (q < 0) return;
  const params = new URLSearchParams(returnPath.slice(q));
  setAttr('launch.lan', params.get('lan'));
  setAttr('launch.mode', params.get('mode'));
  const exitUrl = params.get('exitUrl');
  if (!exitUrl) return;
  try {
    // exitUrl is partner-controlled; its own query says which partner page the player came from.
    const exit = new URL(exitUrl);
    setAttr('launch.exitHost', exit.host);
    setAttr('launch.entryPage', exit.searchParams.get('page'));
    setAttr('launch.bonusId', exit.searchParams.get('bonusId'));
  } catch {
    /* unparsable exitUrl is not our problem */
  }
};

const probeThirdPartyCookie = (): boolean | undefined => {
  try {
    const name = 'pg_launch_probe';
    document.cookie = `${name}=1; Max-Age=60; Path=/; SameSite=None; Secure`;
    const ok = document.cookie.indexOf(`${name}=1`) >= 0;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=None; Secure`;
    return ok;
  } catch {
    return undefined;
  }
};

const collectDevice = (): void => {
  try {
    const nav = navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number } };
    setAttr('browser.userAgent', nav.userAgent);
    setAttr('browser.mobile', isMobile());
    setAttr('browser.language', nav.language);
    setAttr('browser.cookiesEnabled', nav.cookieEnabled);
    const isIframe = window.self !== window.top;
    setAttr('page.isIframe', isIframe);
    if (isIframe) setAttr('browser.thirdPartyCookies', probeThirdPartyCookie());
    setAttr('page.topOrigin', getTopOrigin());
    setAttr('page.referrer', document.referrer);
    setAttr('page.host', window.location.host);
    setAttr('screen.w', window.screen?.width);
    setAttr('screen.h', window.screen?.height);
    setAttr('net.effectiveType', nav.connection?.effectiveType);
    if (nav.connection?.downlink !== undefined) setAttr('net.downlink', String(nav.connection.downlink));
  } catch {
    /* best effort */
  }
};

const attr = (key: string, value: AttrValue) => {
  if (typeof value === 'boolean') return { key, value: { boolValue: value } };
  if (typeof value === 'number') return { key, value: { intValue: String(Math.round(value)) } };
  return { key, value: { stringValue: value } };
};

// Must match the backend's `deployment.environment` values; SigNoz's Env filter keys on them.
const environmentName = (): string => {
  switch (env.type) {
    case EnvironmentTypes.Production:
      return 'Production';
    case EnvironmentTypes.Staging:
      return 'Staging';
    default:
      return 'Development';
  }
};

const buildPayload = (outcome: LaunchOutcome) => {
  const end = Date.now();
  setAttr('launch.outcome', outcome);
  setAttr('launch.stalledAt', furthestGameStep());
  setAttr('launch.lastStep', state.lastStep);
  setAttr('launch.errorReason', state.errorReason);
  setAttr('launch.errorMessage', state.errorMessage);
  if (state.errorCount) setAttr('launch.errorCount', state.errorCount);
  setAttr('launch.t.total', end - state.t0Epoch);
  setAttr('page.visibleAtFinish', typeof document !== 'undefined' ? document.visibilityState === 'visible' : undefined);
  (Object.keys(state.steps) as LaunchStep[]).forEach(step => setAttr(`launch.t.${step}`, state.steps[step] as number));

  const span: Record<string, unknown> = {
    traceId: state.traceId,
    spanId: state.spanId,
    name: SPAN_NAME,
    kind: 3,
    startTimeUnixNano: toNano(state.t0Epoch),
    endTimeUnixNano: toNano(end),
    attributes: Object.keys(state.attrs).map(key => attr(key, state.attrs[key])),
    status: outcome === 'game_ready' ? { code: 1 } : { code: 2, message: outcome }
  };
  if (state.parentSpanId) span.parentSpanId = state.parentSpanId;

  return {
    resourceSpans: [{
      resource: {
        attributes: [
          attr('service.name', `rpd-launcher-web${env.production ? '' : '-stage'}`),
          attr('deployment.environment', environmentName()),
          attr('telemetry.sdk.name', 'pascal-launcher-beacon')
        ]
      },
      scopeSpans: [{ scope: { name: 'launch-telemetry', version: '1' }, spans: [span] }]
    }]
  };
};

// Primary path: the already-authenticated SignalR connection (LauncherHub.LaunchTrace). false = no socket.
const trySocket = (payloadJson: string): boolean => {
  try {
    return !!state.traceSender && state.traceSender(payloadJson);
  } catch {
    return false;
  }
};

const send = (body: string): void => {
  if (!env.otlpUrl) {
    // Local dev: payload to the console, but still mark completion on the hub so stage emits no disconnect span.
    if (!env.production) console.debug('[launch-telemetry]', JSON.parse(body));
    trySocket('');
    return;
  }
  if (trySocket(body)) return;
  // HTTP rescue only when there is no socket. keepalive lets the request outlive the page;
  // sendBeacon cannot carry either of these headers.
  try {
    if (state.authToken) {
      fetch(env.otlpUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${state.authToken}` },
        body,
        keepalive: true,
        credentials: 'omit'
      }).catch(() => undefined);
      return;
    }
    // finishLaunch('error') after a launch/info 400, and finishLaunch('unavailable') for maintenance
    // and coming-soon, both fire before any socket or player token exists. Without this the beacon has
    // no transport and those launches sit inside the successful denominator.
    if (state.launchToken) {
      fetch(env.otlpUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Launch-Token': state.launchToken },
        body,
        keepalive: true,
        credentials: 'omit'
      }).catch(() => undefined);
    }
  } catch {
    /* ignore */
  }
};

// persisted = page goes to the back/forward cache and may come back; do not call that launch abandoned.
const onPageHide = (event: PageTransitionEvent): void => {
  if (!event?.persisted) finishLaunch('abandoned');
};
const onWindowError = (event: ErrorEvent): void => failLaunch('js', event?.message || 'error');
const onRejection = (event: PromiseRejectionEvent): void => {
  // Cancelled because the page is going away, not failed. See http-service onRejected.
  const reason = event?.reason as { aborted?: boolean } | undefined;
  if (reason?.aborted) return;
  failLaunch('js_unhandled_rejection', event?.reason);
};

const armWatchdog = (): void => {
  if (state.watchdog) clearTimeout(state.watchdog);
  state.watchdog = setTimeout(() => finishLaunch('stalled'), WATCHDOG_MS);
};

const detach = (): void => {
  if (state.watchdog) {
    clearTimeout(state.watchdog);
    state.watchdog = null;
  }
  window.removeEventListener('pagehide', onPageHide);
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onRejection);
};

/** Call once at SPA boot with the parsed launch query (`traceparent`, `launchToken`, `returnPath`, ...). */
export const beginLaunchTelemetry = (query: Record<string, unknown>): void => {
  try {
    if (state.started) return;
    state.started = true;
    state.t0Epoch = Date.now();
    state.t0Perf = now();

    const tp = TRACEPARENT.exec(String(query.traceparent || ''));
    state.traceId = tp ? tp[1].toLowerCase() : randomHex(16);
    state.parentSpanId = tp ? tp[2].toLowerCase() : '';
    state.spanId = randomHex(8);
    state.launchToken = String(query.launchToken || '');
    // Success sampling is decided up front so API-call parenting and the beacon agree (see getTraceparentHeader).
    state.sampled = Math.random() < env.telemetrySampleRate;

    setAttr('launch.traceparentPresent', !!tp);
    setAttr('launcher.launchToken', state.launchToken);
    setAttr('launcher.supportCode', query.supportCode as string);
    setAttr('launch.launchError', query.launchError as string);
    collectReturnPath(query.returnPath);
    collectDevice();

    armWatchdog();
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onRejection);
  } catch {
    /* telemetry must never break a launch */
  }
};

/** Records the time (ms since boot) a launch step was first reached. */
export const markLaunchStep = (step: LaunchStep): void => {
  try {
    if (!state.started || state.finished || state.steps[step] !== undefined) return;
    state.steps[step] = Math.round(now() - state.t0Perf);
    state.lastStep = step;
    armWatchdog();
  } catch {
    /* ignore */
  }
};

export const setLaunchContext = (data: Partial<LaunchData>): void => {
  try {
    if (!state.started || state.finished) return;
    setAttr('launcher.userId', data.userId);
    setAttr('launcher.playerExternalId', data.playerExternalId);
    setAttr('launcher.partnerId', data.partnerId);
    setAttr('launcher.gameId', data.gameId);
    setAttr('launcher.currency', data.currency);
    setAttr('launcher.culture', data.culture);
    setAttr('launcher.launchType', data.launchType);
    setAttr('launcher.category', data.category);
    setAttr('auth.isAuthenticated', data.isAuthenticated);
    setAttr('game.hasPreLobby', data.hasPreLobby);
    setAttr('game.hasJackpot', data.hasJackpot);
    setAttr('game.hasCashback', data.hasCashback);
  } catch {
    /* ignore */
  }
};

/** Free-form attribute on the current launch span, for callers outside this module. */
export const setLaunchAttr = (key: string, value: AttrValue): void => {
  try {
    if (state.started && !state.finished) state.attrs[key] = value;
  } catch {
    /* ignore */
  }
};

export const setLaunchIframeSource = (src: string): void => {
  try {
    setAttr('game.iframeHost', new URL(src, window.location.href).host);
  } catch {
    /* ignore */
  }
};

/**
 * The game document comes from the Cloudflare edge and never passes our nginx, so no log of ours can
 * see it. The client can: this separates "the edge was slow to answer" from "the bundle is big".
 */
export const recordFrameTiming = (frame: HTMLIFrameElement | null): void => {
  try {
    const nav = frame?.contentWindow?.performance
      ?.getEntriesByType('navigation')?.[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return;                                   // cross-origin or unsupported: skip silently
    setAttr('frame.dns', Math.round(nav.domainLookupEnd - nav.domainLookupStart));
    setAttr('frame.connect', Math.round(nav.connectEnd - nav.connectStart));
    setAttr('frame.tls', nav.secureConnectionStart
      ? Math.round(nav.connectEnd - nav.secureConnectionStart) : 0);
    setAttr('frame.ttfb', Math.round(nav.responseStart - nav.startTime));
    setAttr('frame.download', Math.round(nav.responseEnd - nav.responseStart));
    setAttr('frame.transferSize', nav.transferSize || 0);
    setAttr('frame.encodedSize', nav.encodedBodySize || 0);
  } catch {
    /* cross-origin: never throw on the launch path */
  }
};

/** Records a failure without finishing; the first failure is kept because it is the root cause. */
export const failLaunch = (reason: string, error?: unknown): void => {
  try {
    if (!state.started || state.finished) return;
    state.errorCount += 1;
    if (state.errorReason) return;
    state.errorReason = reason;
    state.errorMessage = describe(error).slice(0, MAX_ERROR_MESSAGE);
  } catch {
    /* ignore */
  }
};

/** Sends the beacon exactly once. Failures are always sent; successes are sampled. */
export const finishLaunch = (outcome: LaunchOutcome): void => {
  try {
    if (!state.started || state.finished) return;
    state.finished = true;
    detach();
    if (outcome === 'game_ready' && !state.sampled) {
      // Completion ping without payload: tells the hub this launch finished, so its disconnect stays quiet.
      trySocket('');
      return;
    }
    send(JSON.stringify(buildPayload(outcome)));
  } catch {
    /* ignore */
  }
};

/**
 * W3C header for API calls. Parent = the backend redirect span when we have one (it always exists in SigNoz),
 * else the beacon span, and only when that beacon will actually be sent, so no backend span is ever orphaned.
 */
export const getTraceparentHeader = (): string | null => {
  if (!state.started) return null;
  if (state.parentSpanId) return `00-${state.traceId}-${state.parentSpanId}-01`;
  return state.sampled ? `00-${state.traceId}-${state.spanId}-01` : null;
};

export const getLaunchToken = (): string => state.launchToken;

/** This launch's trace id; analytics events carry it as `launchId` so they join the launch.web span. */
export const getLaunchTraceId = (): string => state.traceId;

/** After a socket reconnect the new connection has no record of this launch; repeat the completion ping. */
export const resendLaunchCompletion = (): void => {
  try {
    if (state.finished) trySocket('');
  } catch {
    /* ignore */
  }
};

/** Registered by the launcher socket: sends the beacon over SignalR, returns false when not connected. */
export const setLaunchTraceSender = (sender: ((payloadJson: string) => boolean) | null): void => {
  state.traceSender = sender;
};

/** Player access token for the HTTP rescue endpoint; call on every auth update and refresh. */
export const setLaunchAuthToken = (token: string | null | undefined): void => {
  state.authToken = token || '';
};
